import React from 'react';
import { ErrorDisplay } from '@/components/ErrorDisplay';
import { UserRole } from '@/types/user';
import { validateInvestorForm, InvestorFormData } from '@/utils/formValidation/investorForm';
import { useSignupStore } from '../../stores/signupStore';
import { CompanyInfoSection } from './CompanyInfoSection';
import { CompanyRepresentativeSection } from './CompanyRepresentativeSection';
import { IndividualInvestorForm } from './IndividualInvestorForm';
import { InvestorRoleToggle } from './InvestorRoleToggle';
import { SignupStepLayout } from './SignupStepLayout';

type InvestorFormProps = {
  onBack: () => void;
  onNext: () => void;
  onSwitchToLogin?: () => void;
};

const InvestorForm: React.FC<InvestorFormProps> = ({ onBack, onNext, onSwitchToLogin }) => {
  const signupStore = useSignupStore();
  const { formData, errors, submitAttempted } = signupStore.investorForm;
  const { updateInvestorFormData, updateInvestorFormField, setInvestorErrors, setInvestorSubmitAttempted } =
    signupStore;
  const userRole = formData.user_role;

  const handleFieldChange = (field: keyof InvestorFormData, value: string | boolean) => {
    updateInvestorFormField(field, value);
  };

  const getFieldError = (field: keyof InvestorFormData) => {
    // Only show field errors if submit was attempted
    if (!submitAttempted) return undefined;
    return errors[field]?.[0];
  };

  const handleSubmit = async () => {
    setInvestorSubmitAttempted(true);

    // Validate form
    const validation = validateInvestorForm(formData, true);
    if (!validation.isValid) {
      setInvestorErrors(validation.errors);
      return;
    }

    setInvestorErrors({});

    onNext();
  };

  return (
    <SignupStepLayout
      actions={{
        primaryLabel: 'Continue',
        onPrimaryClick: handleSubmit,
        onBack,
      }}
      footer={{
        onSwitchToLogin,
        termsChecked: formData.acceptedTerms,
        onTermsChange: (checked) => handleFieldChange('acceptedTerms', checked),
      }}
      contentVariant="compact"
    >
      <div className="flex flex-col items-center justify-between self-stretch gap-4">
        <InvestorRoleToggle value={userRole} onChange={(role) => updateInvestorFormData({ user_role: role })} />

        {userRole === UserRole.INVESTOR_INDIVIDUAL ? (
          <IndividualInvestorForm formData={formData} onFieldChange={handleFieldChange} getFieldError={getFieldError} />
        ) : (
          <div className="flex w-full max-w-[654px] flex-col gap-8">
            <CompanyRepresentativeSection
              formData={formData}
              onFieldChange={handleFieldChange}
              getFieldError={getFieldError}
            />
            <CompanyInfoSection formData={formData} onFieldChange={handleFieldChange} getFieldError={getFieldError} />
          </div>
        )}

        {submitAttempted && <ErrorDisplay errors={Object.values(errors).flat()} className="text-left" />}
      </div>
    </SignupStepLayout>
  );
};

export default InvestorForm;
